import { roundMoney } from '../../../../../utils/money';
// Summary: Receipt print-preview modal with render lifecycle and print trigger behavior.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-receipt
import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Button } from '../../../../../components/Button';
import { useEmployeeSession } from '../../../../../contexts/EmployeeSessionProvider';
import { printersService } from '../../../../../services/printers/printers.service';
import type { PrinterView } from '../../../../../services/printers/printers.service';
import type { OrderView } from '../../../../../services/orders/orders.service';
import { orderlineDisplayName, type OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import {
  formatOrderDate,
  formatPrintDate,
  getOrderNumberDisplay,
  groupOrderlines,
  captureReceiptSnapshot,
} from '../shared/receipt.utils';
import { receiptService } from '../../../../../services/receipt/receipt.service';

import { formatMoney as formatPosMoney } from '../../../../../utils/money';
import { deriveReceiptTip, formatTipWithPercent } from '../../orders/tip/tip.utils';
import { calculateOrderDiscount } from '../../orders/shared/discount.utils';
import { groupOrderlineSelections, groupOrderlineExtraTotal } from '../../../../../utils/orderline-selections';
import { resolveReceiptAutoPrinterId, resolveReceiptPrintTarget } from '../../../hooks/fixed-printer.util';
import { ReportPrinterPickModal } from '../../reports/ReportPrinterPickModal';
import classes from './receipt-print-preview-modal.module.scss';

export type ReceiptPrintPreviewModalProps = {
  opened: boolean;
  onClose: () => void;
  order: OrderView;
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  largeTableNumberOnReceipt?: boolean;
};

type ReceiptContentProps = {
  order: OrderView;
  headerText: string;
  waiterName: string;
  activeSentLines: OrderlineView[];
  orderDiscount: number;
  billableTotal: number;
  tipAmount: number;
  paidAmount: number;
  remainingAmount: number;
  largeTableNumberOnReceipt: boolean;
};

const DashedSeparator: React.FC = () => (
  <div className={classes.dashedSeparatorContainer} aria-hidden="true">
    <div className={classes.dashedLine} />
  </div>
);

const ReceiptContent: React.FC<ReceiptContentProps> = ({
  order,
  headerText,
  waiterName,
  activeSentLines,
  orderDiscount,
  billableTotal,
  tipAmount,
  paidAmount,
  remainingAmount,
  largeTableNumberOnReceipt,
}) => {
  const formatMoney = (value: number) => formatPosMoney(value, order.roundAmounts ?? false);
  const largeTableNumber = order.orderType !== 'take_away' && order.tableId != null
    ? order.tableNumber ?? order.orderCode
    : null;
  return (
    <>
      {largeTableNumberOnReceipt && largeTableNumber && (
        <div className={classes.largeTableNumber}>שולחן: {largeTableNumber}</div>
      )}
      <div className={classes.headerText}>
        {headerText.split('\n').map((line, index) => (
          <span key={index} className={classes.restaurantHp}>{line}</span>
        ))}
      </div>

      <div className={classes.metaBlock}>
        <div className={classes.metaCenteredRow}>הודפס בתאריך: {formatPrintDate()}</div>
        <div className={classes.metaCenteredRow}>תאריך תחילת הזמנה: {formatOrderDate(order.createdAt)}</div>
      </div>

      <div className={classes.orderPropsGrid}>
        <div className={classes.orderPropsRow}>
          <span>מספר הזמנה {getOrderNumberDisplay(order.orderCode, order.id)}</span>
          <span>{order.orderType === 'take_away' ? 'לקחת' : order.tableId == null ? 'לשבת' : largeTableNumberOnReceipt ? null : `שולחן: ${largeTableNumber}`}</span>
        </div>
        <div className={classes.orderPropsRow}>
          <span>מלצר: {waiterName}</span>
          <span>סועדים: {order.dinersCount}</span>
        </div>
        {order.guestName && (
          <div className={classes.orderPropsCentered}>
            <span>שם לקוח: {order.guestName}</span>
          </div>
        )}
      </div>

      <DashedSeparator />
      <div className={classes.receiptTitle}>חשבון</div>

      {activeSentLines.length === 0 ? (
        <div className={classes.emptyItems}>אין פריטים בחשבון.</div>
      ) : (
        <ul className={classes.itemsList}>
          {activeSentLines.map((line) => {
            const selectionGroups = groupOrderlineSelections((line.selections ?? []).filter((s) => s.groupType === 'combo'));
            return (
              <React.Fragment key={line.id}>
                <li className={classes.itemRow}>
                  <div className={classes.itemRight}>
                    <span className={classes.itemQty}>{line.quantity}</span>

                    <span className={classes.itemName}>{orderlineDisplayName(line)}</span>
                  </div>
                  <span className={classes.itemPrice}>{formatMoney(line.billableAmount ?? (line.unitPrice * line.quantity - (line.discountInput ?? 0)))}</span>
                </li>
                {selectionGroups.map((group, index) => (
                  <li key={`${line.id}-sub-${index}`} className={`${classes.itemRow} ${classes.itemSubRow}`}>
                    <div className={classes.itemRight}>
                      <span className={classes.itemQty} />
                      <span className={classes.itemName}>
                        <span className={classes.selectionGroupLabel}>{group.groupName}: </span>
                        {group.options.map((opt) => opt.name).join(', ')}
                      </span>
                    </div>
                    <span className={classes.itemPrice}>+{formatMoney(groupOrderlineExtraTotal(group, line.quantity))}</span>
                  </li>
                ))}
              </React.Fragment>
            );
          })}
          {orderDiscount > 0 && (
            <li className={classes.itemRow}>
              <div className={classes.itemRight}>
                <span className={classes.itemQty} />
                <span className={classes.itemName}>הנחה</span>
              </div>
              <span className={classes.itemPrice}>-{formatMoney(orderDiscount)}</span>
            </li>
          )}
        </ul>
      )}

      <DashedSeparator />

      <div className={classes.calcBlock}>
        <div className={classes.totalRowBold}>
          <span>סה"כ הזמנה</span>
          <span>{formatMoney(billableTotal)}</span>
        </div>
      </div>

      <DashedSeparator />

      <div className={classes.totalsBlock}>
        {tipAmount > 0 && (
          <div className={classes.totalRowNormal}>
            <span>טיפ</span>
            <span dir="ltr">{formatTipWithPercent(tipAmount, billableTotal, order.roundAmounts ?? false)}</span>
          </div>
        )}
        <div className={classes.totalRowNormal}>
          <span>סה״כ שולם</span>
          <span>{formatMoney(paidAmount)}</span>
        </div>
        <div className={classes.totalRowBold}>
          <span>יתרה לתשלום</span>
          <span>{formatMoney(remainingAmount)}</span>
        </div>
      </div>

      <div className={classes.deliveryTypeRow}>
        <span>{order.orderType === 'take_away' ? 'לקחת' : 'לשבת'}</span>
      </div>

      {order.orderType !== 'take_away' && (
        <div className={classes.tipsBlock}>
          <span>Fair 10%</span>
          <span>Good 15%</span>
          <span>Excellent 20%</span>
        </div>
      )}

      <div className={classes.poweredBy}>
        Powered by CommunicateAI
      </div>
    </>
  );
};

const ReceiptPrintPreviewModalComponent: React.FC<ReceiptPrintPreviewModalProps> = ({
  opened,
  onClose,
  order,
  orderlines,
  paymentlines,
  largeTableNumberOnReceipt = false,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const { employeeState } = useEmployeeSession();
  const employeeSession = employeeState?.employeeSession;
  const tenantId = order.tenantId;

  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(() => {
    return localStorage.getItem('posReceiptPrinterId') ?? null;
  });
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printerPickOpen, setPrinterPickOpen] = useState(false);
  const [pickCandidates, setPickCandidates] = useState<PrinterView[]>([]);
  const printInFlightRef = useRef(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [vatRatePercent, setVatRatePercent] = useState(0);
  const [headerText, setHeaderText] = useState('');
  const [waiterName, setWaiterName] = useState('--');

  useEffect(() => {
    if (!opened) {
      setCapturedImage(null);
      return;
    }
    let cancelled = false;
    void captureReceiptSnapshot(receiptRef.current).then((url) => {
      if (cancelled) return;
      setCapturedImage(url ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [opened]);

  useEffect(() => {
    if (opened && employeeSession) {
      setLoading(true);
      printersService
        .listActive(tenantId, employeeSession)
        .then((data) => {
          setPrinters(data);
          const stored = localStorage.getItem('posReceiptPrinterId');
          if (stored && data.some((p) => String(p.id) === stored)) {
            setSelectedPrinterId(stored);
          } else if (data.length > 0) {
            const firstId = String(data[0].id);
            setSelectedPrinterId(firstId);
            localStorage.setItem('posReceiptPrinterId', firstId);
          }
        })
        .catch((err) => {
          console.error('Failed to load printers:', err);
          notifications.show({
            color: 'red',
            title: 'שגיאה',
            message: 'שגיאה בטעינת רשימת המדפסות הפעילות',
            autoClose: 3000,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [opened, tenantId, employeeSession]);

  useEffect(() => {
    if (!opened || !employeeSession) return;
    let cancelled = false;
    void receiptService
      .getTenantVatRate(tenantId, employeeSession, order.id)
      .then(({ vatRatePercent: rate, header, openedByName }) => {
        if (cancelled) return;
        setVatRatePercent(rate);
        setHeaderText(header);
        setWaiterName(openedByName ?? '--');
      })
      .catch(() => {
        if (!cancelled) {
          setVatRatePercent(0);
          setWaiterName('--');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [opened, tenantId, employeeSession, order.id]);

  // Take-away bills its still-pending lines too (paid upfront, sent to kitchen at close).
  const activeSentLines = React.useMemo(
    () => orderlines.filter(
      (line) =>
        line.status === 'sent' ||
        line.status === 'skipped' ||
        (order.orderType === 'take_away' && line.status === 'pending'),
    ),
    [orderlines, order.orderType],
  );
  const groupedSentLines = React.useMemo(() => groupOrderlines(activeSentLines), [activeSentLines]);
  // Items net of per-line discounts; order discount applies to the subtotal. VAT on billable.
  const itemsSubtotal = activeSentLines.reduce(
    (sum, line) => sum + roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), order.roundAmounts),
    0,
  );
  const orderDiscount = calculateOrderDiscount(itemsSubtotal, order.discountMode, order.discountInput, order.roundAmounts);
  const billableTotal = Math.max(0, itemsSubtotal - orderDiscount);
  // VAT base is billableTotal (order discount is pre-tax); 0% rate yields no split.
  // Round to match backend (one-decimal half-up) so ex-VAT + VAT == the displayed total.
  const { tip: tipAmount, paidTip } = deriveReceiptTip(order, paymentlines, billableTotal);

  // Paid includes the tip collected on done lines, so the remaining nets it out too.
  const paidAmount = paymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce((sum, line) => sum + line.amount, 0) + paidTip;

  const remainingAmount = Math.max(0, billableTotal + tipAmount - paidAmount);

  // Billable printer (or the 0-billable device/sole fallback) auto-targets and hides the picker.
  const autoPrinterId = resolveReceiptAutoPrinterId(printers);
  const effectivePrinterId = autoPrinterId != null ? String(autoPrinterId) : selectedPrinterId;

  const handlePrint = async (printerIdOverride?: string) => {
    // Idempotency guard: the printer-picker path (handlePrinterPicked) calls this, not via the Button.
    if (printInFlightRef.current) {
      return;
    }

    const printerId = printerIdOverride ?? effectivePrinterId;
    if (!printerId) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: 'לא נמצאה מדפסת פעילה במערכת',
        autoClose: 3000,
      });
      return;
    }

    if (!employeeSession) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: 'שגיאת הרשאה: סשן פקע או לא קיים',
        autoClose: 3000,
      });
      return;
    }

    const element = receiptRef.current;
    if (!element) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: 'אלמנט התצוגה המקדימה לא נמצא',
        autoClose: 3000,
      });
      return;
    }

    try {
      printInFlightRef.current = true;
      setIsPrinting(true);

      const base64Image = capturedImage ?? await captureReceiptSnapshot(element);
      if (!base64Image) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: 'לא ניתן ליצור תמונה להדפסה',
          autoClose: 3000,
        });
        return;
      }

      await printersService.queueOrderReceiptPrint(
        tenantId,
        order.id,
        Number(printerId),
        base64Image,
        employeeSession,
        true,
      );

      notifications.show({
        color: 'green',
        title: 'הדפסה',
        message: 'החשבון נשלח להדפסה בהצלחה במדפסת שנבחרה',
        autoClose: 3000,
      });

      onClose();
    } catch (err: unknown) {
      console.error('Receipt print failed:', err);
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: err instanceof Error ? err.message : 'שגיאה לא ידועה בעת שליחת החשבון להדפסה',
        autoClose: 3000,
      });
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
    }
  };

  const persistPrinter = (id: string) => {
    setSelectedPrinterId(id);
    localStorage.setItem('posReceiptPrinterId', id);
  };

  // Fixed/sole printer prints silently; multi-printer opens the shared pick modal.
  const requestPrint = () => {
    const target = resolveReceiptPrintTarget(printers);
    if (target.kind === 'print') {
      const val = String(target.printerId);
      persistPrinter(val);
      void handlePrint(val);
      return;
    }
    if (target.kind === 'pick') {
      // 2+ billable → restrict the picker to the billable candidates; else the full active list.
      setPickCandidates(target.candidates ?? printers);
      setPrinterPickOpen(true);
      return;
    }
    void handlePrint();
  };

  const handlePrinterPicked = (printerId: number) => {
    setPrinterPickOpen(false);
    const val = String(printerId);
    persistPrinter(val);
    void handlePrint(val);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="sm"
      centered
      radius="md"
      dir="rtl"
      withCloseButton={false}
      classNames={{ root: classes.modalRoot }}
    >
      <div className={classes.receiptPaper} data-testid="receipt-preview-modal">
        <div className={classes.receiptScrollArea}>
          <ReceiptContent
            order={order}
            headerText={headerText}
            waiterName={waiterName}
            activeSentLines={groupedSentLines}
            orderDiscount={orderDiscount}
            billableTotal={billableTotal}
            tipAmount={tipAmount}
            paidAmount={paidAmount}
            remainingAmount={remainingAmount}
            largeTableNumberOnReceipt={largeTableNumberOnReceipt}
          />
        </div>

        {!loading && printers.length === 0 && (
          <span className={classes.printerPickerEmpty}>אין מדפסות פעילות</span>
        )}

        <div className={classes.buttonRow}>
          <Button variant="cancel" fullWidth onClick={onClose} disabled={isPrinting} dataTestId="receipt-preview-cancel">
            ביטול
          </Button>
          <Button
            variant="confirm"
            fullWidth
            onClick={() => requestPrint()}
            disabled={isPrinting || loading || printers.length === 0}
            dataTestId="receipt-preview-print-button"
          >
            {isPrinting ? 'מדפיס...' : 'הדפס'}
          </Button>
        </div>
      </div>

      <div className={classes.printCaptureOffscreenRow}>
        <div className={classes.offscreenReceiptReady} ref={receiptRef}>
          <ReceiptContent
            order={order}
            headerText={headerText}
            waiterName={waiterName}
            activeSentLines={groupedSentLines}
            orderDiscount={orderDiscount}
            billableTotal={billableTotal}
            tipAmount={tipAmount}
            paidAmount={paidAmount}
            remainingAmount={remainingAmount}
            largeTableNumberOnReceipt={largeTableNumberOnReceipt}
          />
        </div>
      </div>

      {printerPickOpen && (
        <ReportPrinterPickModal
          opened
          printers={pickCandidates}
          disabled={isPrinting}
          onConfirm={handlePrinterPicked}
          onClose={() => setPrinterPickOpen(false)}
        />
      )}
    </Modal>
  );
};

export const ReceiptPrintPreviewModal = React.memo(ReceiptPrintPreviewModalComponent);
